import type {ComponentType} from 'react';
import type {ManualFrontmatter, ManualPage} from './types.ts';

interface MdxModule {
  default: ComponentType;
  frontmatter?: ManualFrontmatter;
}

/**
 * The MDX single-source-of-truth lives in `libs/am-app-manual/docs/content/<lang>/`.
 * These globs are resolved at build time by Vite + @mdx-js/rollup, one entry per page.
 */
const modulesByLang: Record<string, Record<string, MdxModule>> = {
  en: import.meta.glob<MdxModule>('../docs/content/en/*.mdx', {eager: true}),
  cz: import.meta.glob<MdxModule>('../docs/content/cz/*.mdx', {eager: true}),
  sl: import.meta.glob<MdxModule>('../docs/content/sl/*.mdx', {eager: true}),
};

// URL slug = filename without the `.mdx` extension and without the leading `NN-` ordinal.
// The number stays in the filename purely for ordering (and `sidebar_position` drives sort);
// the URL is clean, e.g. content/en/04-validation-console.mdx -> /validation-console.
const slugFromPath = (path: string): string =>
  (path.split('/').pop() ?? path).replace(/\.mdx$/, '').replace(/^\d+-/, '');

const toPages = (modules: Record<string, MdxModule>): ManualPage[] =>
  Object.entries(modules)
    .map(([path, mod]) => ({
      slug: slugFromPath(path),
      frontmatter: mod.frontmatter ?? {title: slugFromPath(path)},
      Component: mod.default,
    }))
    .sort((a, b) => (a.frontmatter.sidebar_position ?? 999) - (b.frontmatter.sidebar_position ?? 999));

const pagesByLang: Record<string, ManualPage[]> = Object.fromEntries(
  Object.entries(modulesByLang).map(([lang, modules]) => [lang, toPages(modules)]),
);

const DEFAULT_LANG = 'en';

if ((pagesByLang[DEFAULT_LANG] ?? []).length === 0) {
  throw new Error('am-app-manual: no MDX content discovered in libs/am-app-manual/docs/content/en/*.mdx');
}

// Date-based manual version, loaded from the content SSoT (via glob, to respect module boundaries).
const versionModules = import.meta.glob('../docs/content/version.json', {
  eager: true,
}) as Record<string, {version?: string; default?: {version?: string}}>;
const versionModule = Object.values(versionModules)[0];
const UNKNOWN_VERSION = 'unknown';
const resolvedVersion = versionModule?.version ?? versionModule?.default?.version;
if (!resolvedVersion) {
  // eslint-disable-next-line no-console
  console.warn('version.json missing.');
}
export const manualVersion: string = resolvedVersion ?? UNKNOWN_VERSION;

/** Pages for a language, falling back to English when a translation has no content yet. */
export const getPages = (lang: string): ManualPage[] => {
  const pages = pagesByLang[lang];
  return pages?.length ? pages : (pagesByLang[DEFAULT_LANG] ?? []);
};

export const getPage = (lang: string, slug: string): ManualPage | undefined =>
  getPages(lang).find(p => p.slug === slug);

export const getFirstSlug = (lang: string): string => getPages(lang)[0]?.slug ?? '';
